import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Input } from "@mui/material";
import { t } from "i18next";
import { Close } from "@mui/icons-material";

import {
  removeAndAddMessages,
  getAppSSID,
  getConversationID,
  selectPropertyId,
  getOrgId,
  selectIsFaq,
  addNewMessage,
  selectIsDisabled,
  setIsDisabled,
  getEnabledScheduleTour,
  selectAPIKey,
  setShowAllOptions,
  getShowAllOptions,
  getHeaderBgColor,
  selectMessages,
  getMultiLanguage,
  getSpanishLanguage,
} from "slices";
import { Icon, CustomTooltip, OptionsBox } from "components";
import { getFAQsReply } from "utils";
import { DEFAULT_SESSION_ID, ERROR_MSG, INITIAL_REPLIES } from "utils/constants/index";
import { setReplies } from "slices/replies-slice/index";
import { getEntryId } from "slices/property-slice/index";
import { getLastConversations, handleAMAResponse } from "utils/methods/index";

const AMAOptions = () => {
  const dispatch = useDispatch();
  const enabledScheduleTour = useSelector(getEnabledScheduleTour);
  const messages = useSelector(selectMessages);

  const SSID = useSelector(getAppSSID);
  const conversationID = useSelector(getConversationID);
  const propertyId = useSelector(selectPropertyId);
  const orgId = useSelector(getOrgId);
  const apiKey = useSelector(selectAPIKey);
  const isFaq = useSelector(selectIsFaq);
  const optionDisabled = useSelector(selectIsDisabled);
  const showAllOptions = useSelector(getShowAllOptions);
  const backgroundColor = useSelector(getHeaderBgColor);
  const entryId = useSelector(getEntryId);
  const isMultiLanguageEnabled = useSelector(getMultiLanguage);
  const isSpanishCurrentLanguage = useSelector(getSpanishLanguage);

  const [inputValue, setInputValue] = useState("");
  const [disabled, setDisabled] = useState(false);

  useEffect(() => {
    if (inputValue === "") {
      setDisabled(true);
    } else {
      setDisabled(false);
    }
  }, [inputValue, isFaq]);

  const onChange = (e) => {
    setInputValue(e.target.value);
  };

  const onFAQs = (propertyId) => {
    const bookingId = localStorage.getItem(`${propertyId}-bookingId`) || "";
    const storedContext = localStorage.getItem(`${propertyId}-context`);
    const localStorageContext = JSON.parse(storedContext) || [];

    dispatch(
      addNewMessage([
        { message: inputValue, speaker: "user", createdAt: new Date().toISOString() },
        { message: `typing`, speaker: "bot", createdAt: new Date() },
      ])
    );

    localStorage.setItem(`${propertyId}-userFaq-msg`, inputValue);

    setInputValue("");
    dispatch(setIsDisabled(true));

    const history = getLastConversations(messages, 5)
      ?.map((i) => ({
        role: i.speaker == "bot" || i.speaker == "faqBot" ? "assistant" : "user",
        content: i.message,
      }))
      .filter((i) => i.content && i.content.trim() !== "");

    let bookingPayload = null;

    if (
      bookingId &&
      localStorageContext &&
      localStorageContext.length > 0 &&
      localStorageContext?.[0]?.subject === "schedule_a_tour" &&
      localStorageContext?.[0]?.booking_found &&
      !localStorageContext?.[0]?.required_info
    ) {
      bookingPayload = bookingId;
    }

    const faqReply = {
      history,
      propertyId,
      orgId: orgId,
      message: inputValue,
      conversation_id: conversationID || "",
      session_id: SSID || DEFAULT_SESSION_ID,
      graphql_api_key: encodeURIComponent(apiKey),
      tourScheduleEnabled: enabledScheduleTour,
      entry_id: String(entryId ?? ""),
      spanish_support_enabled: !!isMultiLanguageEnabled,
      spanish_toggle: !!isSpanishCurrentLanguage,
      ...(bookingPayload && { booking_id: bookingPayload }),
    };

    getFAQsReply(faqReply)
      .then((res) => {
        handleAMAResponse(res);
      })
      .catch(async (err) => {
        dispatch(removeAndAddMessages([{ message: ERROR_MSG, speaker: "bot", subject: "", createdAt: new Date() }]));
        dispatch(setIsDisabled(false));
      });

    dispatch(setReplies(INITIAL_REPLIES));
  };

  const onClickSend = () => {
    if (propertyId && !disabled && !optionDisabled) {
      dispatch(setShowAllOptions(false));
      onFAQs(propertyId);
    }
  };

  const onKeyPress = (e) => {
    if (e.key === "Enter" && !disabled && !optionDisabled) {
      onClickSend();
    }
  };

  const onClickExploreMore = () => {
    if (!optionDisabled) {
      dispatch(setShowAllOptions(!showAllOptions));
    }
  };

  return (
    <>
      {showAllOptions ? (
        <div>
          <OptionsBox fromAMA={true} />
        </div>
      ) : null}

      <div className="hayley-flex hayley-justify-between hayley-p-4">
        <div
          className={`hayley-flex hayley-h-12 hayley-w-full hayley-items-center hayley-justify-between hayley-rounded-3xl hayley-border hayley-border-solid hayley-border-custom-6 hayley-pl-4 hayley-pr-3`}
        >
          <span className="hayley-w-full">
            <Input
              data-testid="ama-input"
              className={`hayley-w-full hayley-border-none focus:hayley-outline-none `}
              type={"text"}
              placeholder={`${t("enterQuesHere")}`}
              disabled={false}
              value={inputValue}
              onChange={onChange}
              onKeyDown={onKeyPress}
              disableUnderline
            />
          </span>
          {/* <CustomTooltip title={t("exploreMore")} placement="top"> */}
          <Icon
            iconType="linearSend"
            className="hayley-cursor-pointer"
            onClick={onClickSend}
            opacity={inputValue?.length > 0 && !disabled && !optionDisabled ? "1" : "0.5"}
          />
          {/* </CustomTooltip> */}
        </div>

        <CustomTooltip title={showAllOptions || optionDisabled ? "" : t("exploreMore")} placement="top">
          <span
            className={`hayley-flex hayley-h-12 hayley-w-12 hayley-items-center hayley-justify-center hayley-overflow-hidden ${
              optionDisabled ? "hayley-opacity-50" : "hayley-cursor-pointer"
            }`}
            onClick={onClickExploreMore}
          >
            {showAllOptions ? (
              <Close className="hayley-h-6 hayley-w-6" sx={{ "&:hover": { color: `${backgroundColor} !important` } }} />
            ) : (
              <Icon iconType={"moreIcon"} className="" />
            )}
          </span>
        </CustomTooltip>
      </div>
    </>
  );
};

export default AMAOptions;
